<template>
    <div class="min-h-screen bg-gray-50">
        <div class="container mx-auto p-2 sm:p-6">
            <div class="bg-white rounded-xl shadow-sm overflow-hidden">
                <div class="flex justify-between items-center p-3 border-b">
                    <h2 class="text-sm sm:text-base font-semibold">VMD 요청사항</h2>
                </div>
                <div class="divide-y divide-gray-100">
                    <!-- 로딩 상태 -->
                    <div v-if="isLoading" class="p-6 flex justify-center items-center">
                        <v-progress-circular indeterminate color="primary"></v-progress-circular>
                    </div>
                    <!-- VMD 요청사항 목록 -->
                    <template v-else>
                        <div v-for="request in paginatedRequests" :key="request.id" @click="openDetail(request)"
                            class="group hover:bg-blue-50 transition-all duration-200 cursor-pointer">
                            <div class="p-3">
                                <div class="flex items-center justify-between">
                                    <div class="flex-1">
                                        <div class="flex items-center space-x-2">
                                            <span :class="getStatusClass(request.status_code)"
                                                class="px-1.5 py-0.5 rounded-full text-[10px] sm:text-xs font-medium">
                                                {{ getStatusText(request.status_code) }}
                                            </span>
                                            <h3
                                                class="text-[13px] sm:text-[15px] font-medium group-hover:text-blue-600 truncate max-w-[200px] sm:max-w-none">
                                                {{ request.title }}
                                            </h3>
                                        </div>
                                        <div
                                            class="mt-1 flex items-center space-x-3 text-[11px] sm:text-xs text-gray-500">
                                            <span class="flex items-center">
                                                <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 mr-1" fill="none"
                                                    viewBox="0 0 24 24" stroke="currentColor">
                                                    <path stroke-linecap="round" stroke-linejoin="round"
                                                        stroke-width="2"
                                                        d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                                </svg>
                                                {{ request.admin_name }}
                                            </span>
                                            <span class="flex items-center">
                                                <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 mr-1" fill="none"
                                                    viewBox="0 0 24 24" stroke="currentColor">
                                                    <path stroke-linecap="round" stroke-linejoin="round"
                                                        stroke-width="2"
                                                        d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                                </svg>
                                                {{ formatDate(request.date) }}
                                            </span>
                                            <span v-if="request.due_date" class="flex items-center text-red-500">
                                                <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 mr-1" fill="none"
                                                    viewBox="0 0 24 24" stroke="currentColor">
                                                    <path stroke-linecap="round" stroke-linejoin="round"
                                                        stroke-width="2"
                                                        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                </svg>
                                                {{ formatDate_mmdd(request.due_date) }}
                                            </span>
                                        </div>
                                    </div>
                                    <div class="flex items-center ">
                                        <button
                                            v-if="['안읽음', '미제출', '보완요청'].includes(getStatusText(request.status_code))"
                                            @click.stop="openDetail(request)"
                                            class="inline-flex items-center px-2 py-1 sm:px-3 sm:py-1.5 text-[10px] sm:text-xs font-medium text-white bg-blue-500 rounded-md shadow-sm hover:bg-blue-600 hover:shadow-md transition-all duration-200">
                                            제출
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </template>
                </div>
                <div class="flex justify-center items-center p-3 border-t">
                    <v-pagination v-model="currentPage" :length="totalPages" :total-visible="getTotalVisible"
                        density="compact" color="grey-darken-1" class="pagination-text" size="small"
                        @update:model-value="handlePageChange"></v-pagination>
                </div>
            </div>
        </div>

        <!-- VMD 요청사항 상세 보기 사이드 패널 -->
        <transition name="fade">
            <div v-if="selectedRequest" class="fixed inset-0 z-40" @click="selectedRequest = null"></div>
        </transition>
        <transition name="slide">
            <div v-if="selectedRequest"
                class="fixed inset-y-0 right-0 w-full sm:w-[80%] md:w-[70%] lg:w-[60%] xl:w-[50%] 2xl:w-[45%] bg-white shadow-2xl overflow-y-auto z-50">
                <div class="p-4 sm:p-6">
                    <div class="flex justify-between items-center mb-4 sm:mb-6">
                        <div class="flex items-center gap-3">
                            <h2 class="text-base sm:text-lg md:text-xl lg:text-2xl font-bold text-gray-800">{{
                                selectedRequest.title }}</h2>
                            <span :class="getStatusClass(selectedRequest.status_code)"
                                class="px-2 py-1 rounded-full text-[10px] sm:text-xs font-medium">
                                {{ getStatusText(selectedRequest.status_code) }}
                            </span>
                        </div>
                        <button @click="selectedRequest = null" class="text-gray-500 hover:text-gray-700">
                            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 sm:h-5 sm:w-5 md:h-6 md:w-6"
                                fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                    d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>

                    <div class="flex items-center space-x-4 mb-4 sm:mb-6">
                        <span class="flex items-center text-xs sm:text-sm md:text-base text-gray-500">
                            <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 sm:h-4 sm:w-4 mr-1" fill="none"
                                viewBox="0 0 24 24" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                            제출일: {{ formatDate(selectedRequest.date) }}
                        </span>
                        <span v-if="selectedRequest.due_date"
                            class="flex items-center text-xs sm:text-sm md:text-base text-red-500">
                            <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 sm:h-4 sm:w-4 mr-1" fill="none"
                                viewBox="0 0 24 24" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            {{ formatDate(selectedRequest.due_date) }}
                        </span>
                        <span v-if="selectedRequest.admin_name"
                            class="flex items-center text-xs sm:text-sm md:text-base text-gray-500">
                            <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 sm:h-4 sm:w-4 mr-1" fill="none"
                                viewBox="0 0 24 24" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                    d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                            </svg>
                            {{ selectedRequest.admin_name }}
                        </span>
                    </div>

                    <!-- 내용 접기/펼치기 버튼 -->
                    <div class="mb-4">
                        <button @click="toggleContent"
                            class="inline-flex items-center px-3 py-1.5 sm:px-4 sm:py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors duration-200 shadow-sm hover:shadow-md">
                            <svg xmlns="http://www.w3.org/2000/svg"
                                class="h-4 w-4 sm:h-5 sm:w-5 mr-2 transform transition-transform duration-200"
                                :class="{ 'rotate-180': !isContentExpanded }" fill="none" viewBox="0 0 24 24"
                                stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                    d="M19 9l-7 7-7-7" />
                            </svg>
                            <span class="font-medium text-xs sm:text-sm md:text-base">{{ isContentExpanded ? '내용 접기' :
                                '내용 펼치기'
                            }}</span>
                        </button>
                    </div>

                    <!-- 내용 영역 -->
                    <div v-show="isContentExpanded"
                        class="prose max-w-none text-xs sm:text-sm md:text-base lg:text-base"
                        v-html="selectedRequest.content">
                    </div>

                    <!-- 제출 폼 -->
                    <div v-if="['안읽음', '미제출', '보완요청'].includes(getStatusText(selectedRequest.status_code))"
                        class="mt-6 border-t pt-6">
                        <h3 class="text-sm sm:text-base md:text-lg font-semibold mb-4">제출하기</h3>
                        <div class="space-y-4">
                            <!-- 필수1 이미지 -->
                            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label
                                        class="block text-xs sm:text-sm md:text-base font-medium text-gray-700 mb-1">필수1
                                        이미지</label>
                                    <div class="flex">
                                        <div v-for="(image, index) in submitForm.required1" :key="'required1-' + index"
                                            class="relative w-full aspect-square border rounded-md overflow-hidden bg-gray-50">
                                            <img :src="image" class="w-full h-full object-contain">
                                            <button @click="removeRequired1Image(index)"
                                                class="absolute top-2 right-2 bg-red-500 text-white rounded-full w-7 h-7 sm:w-8 sm:h-8 md:w-9 md:h-9 flex items-center justify-center text-base sm:text-lg md:text-xl">
                                                ×
                                            </button>
                                        </div>
                                        <div v-if="submitForm.required1.length < 1"
                                            class="relative w-full aspect-square border-2 border-dashed border-gray-300 rounded-md flex items-center justify-center cursor-pointer hover:border-blue-500 transition-colors duration-200"
                                            @click="triggerRequired1ImageUpload">
                                            <svg xmlns="http://www.w3.org/2000/svg"
                                                class="h-10 w-10 sm:h-12 sm:w-12 md:h-14 md:w-14 text-gray-400"
                                                fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                                    d="M12 4v16m8-8H4" />
                                            </svg>
                                        </div>
                                    </div>
                                    <input type="file" ref="required1ImageInput" class="hidden" accept="image/*"
                                        @change="handleRequired1ImageUpload">
                                </div>
                                <!-- 필수2 이미지 -->
                                <div>
                                    <label
                                        class="block text-xs sm:text-sm md:text-base font-medium text-gray-700 mb-1">필수2
                                        이미지</label>
                                    <div class="flex">
                                        <div v-for="(image, index) in submitForm.required2" :key="'required2-' + index"
                                            class="relative w-full aspect-square border rounded-md overflow-hidden bg-gray-50">
                                            <img :src="image" class="w-full h-full object-contain">
                                            <button @click="removeRequired2Image(index)"
                                                class="absolute top-2 right-2 bg-red-500 text-white rounded-full w-7 h-7 sm:w-8 sm:h-8 md:w-9 md:h-9 flex items-center justify-center text-base sm:text-lg md:text-xl">
                                                ×
                                            </button>
                                        </div>
                                        <div v-if="submitForm.required2.length < 1"
                                            class="relative w-full aspect-square border-2 border-dashed border-gray-300 rounded-md flex items-center justify-center cursor-pointer hover:border-blue-500 transition-colors duration-200"
                                            @click="triggerRequired2ImageUpload">
                                            <svg xmlns="http://www.w3.org/2000/svg"
                                                class="h-10 w-10 sm:h-12 sm:w-12 md:h-14 md:w-14 text-gray-400"
                                                fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                                    d="M12 4v16m8-8H4" />
                                            </svg>
                                        </div>
                                    </div>
                                    <input type="file" ref="required2ImageInput" class="hidden" accept="image/*"
                                        @change="handleRequired2ImageUpload">
                                </div>
                            </div>
                            <!-- 추가 이미지 -->
                            <div>
                                <label class="block text-xs sm:text-sm md:text-base font-medium text-gray-700 mb-1">추가
                                    이미지 (최대
                                    5장)</label>
                                <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                    <div v-for="(image, index) in submitForm.images" :key="'additional-' + index"
                                        class="relative w-full aspect-square border rounded-md overflow-hidden bg-gray-50">
                                        <img :src="image" class="w-full h-full object-contain">
                                        <button @click="removeImage(index)"
                                            class="absolute top-2 right-2 bg-red-500 text-white rounded-full w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center text-base sm:text-lg">
                                            ×
                                        </button>
                                    </div>
                                    <div v-if="submitForm.images.length < 5"
                                        class="relative w-full aspect-square border-2 border-dashed border-gray-300 rounded-md flex items-center justify-center cursor-pointer hover:border-blue-500 transition-colors duration-200"
                                        @click="triggerImageUpload">
                                        <div class="text-center">
                                            <svg xmlns="http://www.w3.org/2000/svg"
                                                class="h-10 w-10 sm:h-12 sm:w-12 text-gray-400 mx-auto mb-2" fill="none"
                                                viewBox="0 0 24 24" stroke="currentColor">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                                    d="M12 4v16m8-8H4" />
                                            </svg>
                                            <span class="text-xs sm:text-sm md:text-base text-gray-500">이미지 추가</span>
                                        </div>
                                    </div>
                                </div>
                                <input type="file" ref="imageInput" class="hidden" accept="image/*" multiple
                                    @change="handleImageUpload">
                            </div>
                            <!-- 기타 텍스트 -->
                            <div>
                                <label
                                    class="block text-xs sm:text-sm md:text-base font-medium text-gray-700 mb-1">코멘트</label>
                                <textarea v-model="submitForm.comment" rows="4"
                                    class="w-full px-3 py-2 text-xs sm:text-sm md:text-base border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"></textarea>
                            </div>
                            <!-- 제출 버튼 -->
                            <div class="flex justify-end">
                                <button @click="handleSubmit"
                                    class="px-3 py-1.5 sm:px-4 sm:py-2 text-xs sm:text-sm md:text-base bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2">
                                    제출하기
                                </button>
                            </div>
                        </div>
                    </div>

                    <!-- 하단 닫기 버튼 -->
                    <div class="sticky bottom-0 left-0 right-0 bg-white border-t p-4 mt-6">
                        <div class="flex flex-col sm:flex-row gap-3">
                            <button v-if="!['미제출', '안읽음'].includes(getStatusText(selectedRequest.status_code))"
                                @click="viewSubmission"
                                class="flex-1 bg-green-500 text-white py-1.5 sm:py-2 px-3 sm:px-4 rounded-lg hover:bg-green-600 transition-colors duration-200 flex items-center justify-center text-xs sm:text-sm md:text-base">
                                <svg xmlns="http://www.w3.org/2000/svg"
                                    class="h-3.5 w-3.5 sm:h-4 sm:w-4 md:h-5 md:w-5 mr-1.5 sm:mr-2" fill="none"
                                    viewBox="0 0 24 24" stroke="currentColor">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                        d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                </svg>
                                제출 내용 보기
                            </button>
                            <button @click="selectedRequest = null"
                                class="flex-1 bg-gray-500 text-white py-1.5 sm:py-2 px-3 sm:px-4 rounded-lg hover:bg-gray-600 transition-colors duration-200 flex items-center justify-center text-xs sm:text-sm md:text-base">
                                <svg xmlns="http://www.w3.org/2000/svg"
                                    class="h-3.5 w-3.5 sm:h-4 sm:w-4 md:h-5 md:w-5 mr-1.5 sm:mr-2" fill="none"
                                    viewBox="0 0 24 24" stroke="currentColor">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                        d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                                </svg>
                                닫기
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </transition>
    </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import vmdAPI from '@/api/vmd/vmd';
import Swal from 'sweetalert2';
import { resizeImage } from '@/utils/imageUtils';
import { useRouter } from 'vue-router';

// 상태 관리
const router = useRouter();
const requests = ref([]);
const totalRequests = ref(0);
const currentPage = ref(1);
const itemsPerPage = 6;
const selectedRequest = ref(null);
const isLoading = ref(false);
const isContentExpanded = ref(true);

// 제출 폼 관련 상태
const submitForm = ref({
    required1: [],
    required2: [],
    images: [],
    comment: '',
    year: new Date().getFullYear(),
    originalFiles: {
        required1: null,
        required2: null,
        additional: []
    }
});

const imageInput = ref(null);
const required1ImageInput = ref(null);
const required2ImageInput = ref(null);

// 메모이제이션된 debounce 함수
const debounce = (fn, delay) => {
    let timeoutId;
    return (...args) => {
        if (timeoutId) clearTimeout(timeoutId);
        timeoutId = setTimeout(() => fn(...args), delay);
    };
};

// 날짜 포맷터
const dateFormatter = new Intl.DateTimeFormat('ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
});

// VMD 요청사항 조회
const fetchRequests = async (page = currentPage.value) => {
    try {
        isLoading.value = true;
        const response = await vmdAPI.getVmdRequests(page, itemsPerPage);
        requests.value = response.data.requests;
        totalRequests.value = response.data.total;

        // URL 해시를 통한 직접 접근인 경우에만 상세조회 열기
        const hash = window.location.hash;
        if (hash && hash.startsWith('#request-') && !window.history.state?.navigatedFromPagination) {
            const requestId = hash.replace('#request-', '');
            const request = requests.value.find(r => r.id === requestId);
            
            if (request) {
                openDetail(request);
            } else {
                // 현재 페이지에서 찾지 못한 경우, 모든 페이지를 검색
                let found = false;
                let currentSearchPage = 1;
                
                while (!found && currentSearchPage <= response.data.totalPages) {
                    if (currentSearchPage !== page) { // 현재 페이지는 이미 검색했으므로 스킵
                        const searchResponse = await vmdAPI.getVmdRequests(currentSearchPage, itemsPerPage);
                        const foundRequest = searchResponse.data.requests.find(r => r.id === requestId);
                        
                        if (foundRequest) {
                            // 해당 페이지로 이동하고 상세보기 열기
                            currentPage.value = currentSearchPage;
                            requests.value = searchResponse.data.requests;
                            openDetail(foundRequest);
                            found = true;
                            break;
                        }
                    }
                    currentSearchPage++;
                }
                
                if (!found) {
                    console.warn('요청사항을 찾을 수 없습니다:', requestId);
                }
            }
        }
    } catch (error) {
        console.error('VMD 요청사항 조회 중 오류 발생:', error);
    } finally {
        isLoading.value = false;
    }
};

// 상세 보기
const openDetail = async (request) => {
    try {
        // 이미 상세 정보가 있는 경우 API 호출 스킵
        if (selectedRequest.value?.id === request.id) {
            return;
        }

        // 상세보기 먼저 열기
        selectedRequest.value = {
            ...request,
            content: '로딩중...' // 임시 컨텐츠
        };
        isContentExpanded.value = true;

        // 안읽음 상태 처리와 상세 정보 조회를 병렬로 처리
        const [detailResponse] = await Promise.all([
            vmdAPI.getVmdRequestDetail(request.id),
            request.status_code === 125 ? updateRequestStatus(request) : Promise.resolve()
        ]);

        // 상세 정보 업데이트
        selectedRequest.value = detailResponse.data;
        window.history.pushState({ requestId: request.id }, '', `#request-${request.id}`);
    } catch (error) {
        console.error('VMD 요청사항 상세 조회 중 오류 발생:', error);
        selectedRequest.value = null;
    }
};

// 요청사항 상태 업데이트 함수 분리
const updateRequestStatus = async (request) => {
    try {
        await vmdAPI.updateStatus(request.id, vmdAPI.VMD_STATUS.CHECKED);
        requests.value = requests.value.map(r => {
            if (r.id === request.id) {
                return {
                    ...r,
                    status_code: vmdAPI.VMD_STATUS.CHECKED
                };
            }
            return r;
        });
    } catch (error) {
        console.error('상태 업데이트 중 오류 발생:', error);
    }
};

// 제출 폼 관련 상태 - 필요할 때만 초기화하도록 함수로 분리
const initSubmitForm = () => {
    submitForm.value = {
        required1: [],
        required2: [],
        images: [],
        comment: '',
        year: new Date().getFullYear(),
        originalFiles: {
            required1: null,
            required2: null,
            additional: []
        }
    };
};

// 상태에 따른 클래스 반환
const getStatusClass = (status_code) => {
    switch (status_code) {
        case 127: // 제출완료
            return 'bg-blue-100 text-blue-800';
        case 129: // 담당자승인
            return 'bg-green-100 text-green-800';
        case 128: // 보완요청
            return 'bg-red-100 text-red-800';
        case 126: // 미제출
            return 'bg-amber-100 text-amber-800';
        case 125: // 안읽음
            return 'bg-gray-100 text-gray-800';
        case 130: // 보완제출
            return 'bg-blue-100 text-blue-800';
        default:
            return 'bg-gray-100 text-gray-800';
    }
};

// 상태 코드에 따른 텍스트 반환 함수 추가
const getStatusText = (status_code) => {
    switch (status_code) {
        case 127:
            return '제출완료';
        case 129:
            return '담당자승인';
        case 128:
            return '보완요청';
        case 126:
            return '미제출';
        case 125:
            return '안읽음';
        case 130:
            return '보완제출';
        default:
            return '알 수 없음';
    }
};

// 페이지네이션 계산
const totalPages = computed(() => Math.ceil(totalRequests.value / itemsPerPage));
const paginatedRequests = computed(() => requests.value);

// 페이지 변경 처리
const handlePageChange = debounce((page) => {
    // 페이지 이동 시 상태 표시
    window.history.replaceState({ navigatedFromPagination: true }, '');
    currentPage.value = page;
    fetchRequests(page);
}, 100);

// 날짜 포맷
const formatDate = (dateString) => {
    if (!dateString) return '';
    return dateFormatter.format(new Date(dateString));
};
const formatDate_mmdd = (dateString) => {
    if (!dateString) return '';
    return new Intl.DateTimeFormat('ko-KR', {
        month: 'numeric',
        day: 'numeric'
    }).format(new Date(dateString));
};

// 페이지네이션 visible 계산
const getTotalVisible = computed(() => {
    const width = window.innerWidth;
    if (width >= 1024) return 7;
    if (width >= 768) return 5;
    return 3;
});

// 뒤로가기 처리 (디바운스 적용)
const handlePopState = debounce(() => {
    if (selectedRequest.value) {
        selectedRequest.value = null;
        history.replaceState(null, '', window.location.pathname);
    }
}, 100);

// 컴포넌트 라이프사이클
onMounted(() => {
    fetchRequests();
    window.addEventListener('popstate', handlePopState);
});

onUnmounted(() => {
    window.removeEventListener('popstate', handlePopState);
});

const triggerImageUpload = () => {
    imageInput.value.click();
};

const triggerRequired1ImageUpload = () => {
    required1ImageInput.value.click();
};

const triggerRequired2ImageUpload = () => {
    required2ImageInput.value.click();
};

const handleImageUpload = async (event) => {
    const files = event.target.files;
    for (let i = 0; i < files.length && submitForm.value.images.length < 5; i++) {
        const file = files[i];
        if (file.type.startsWith('image/')) {
            const resizedFile = await resizeImage(file);
            const reader = new FileReader();
            reader.onload = (e) => {
                submitForm.value.images.push(e.target.result);
                submitForm.value.originalFiles.additional.push(resizedFile);
            };
            reader.readAsDataURL(resizedFile);
        }
    }
};

const handleRequired1ImageUpload = async (event) => {
    const file = event.target.files[0];
    if (file && file.type.startsWith('image/')) {
        const resizedFile = await resizeImage(file);
        const reader = new FileReader();
        reader.onload = (e) => {
            submitForm.value.required1 = [e.target.result];
            submitForm.value.originalFiles.required1 = resizedFile;
        };
        reader.readAsDataURL(resizedFile);
    }
};

const handleRequired2ImageUpload = async (event) => {
    const file = event.target.files[0];
    if (file && file.type.startsWith('image/')) {
        const resizedFile = await resizeImage(file);
        const reader = new FileReader();
        reader.onload = (e) => {
            submitForm.value.required2 = [e.target.result];
            submitForm.value.originalFiles.required2 = resizedFile;
        };
        reader.readAsDataURL(resizedFile);
    }
};

const removeImage = (index) => {
    submitForm.value.images.splice(index, 1);
    submitForm.value.originalFiles.additional.splice(index, 1);
};

const removeRequired1Image = (index) => {
    submitForm.value.required1.splice(index, 1);
    submitForm.value.originalFiles.required1 = null;
};

const removeRequired2Image = (index) => {
    submitForm.value.required2.splice(index, 1);
    submitForm.value.originalFiles.required2 = null;
};

const toggleContent = () => {
    isContentExpanded.value = !isContentExpanded.value;
};

// 제출 처리
const handleSubmit = async () => {
    try {
        if (submitForm.value.required1.length === 0 || submitForm.value.required2.length === 0) {
            Swal.fire({
                icon: 'error',
                title: '실패',
                text: '필수 이미지를 모두 업로드해주세요.',
                customClass: {
                    container: 'swal-responsive-container',
                    icon: 'swal-responsive-icon',
                    title: 'swal-responsive-title',
                    content: 'swal-responsive-content',
                    confirmButton: 'swal-responsive-button',
                    cancelButton: 'swal-responsive-button'
                }
            });
            return;
        }

        // 제출 확인
        const confirmed = await Swal.fire({
            icon: 'warning',
            title: selectedRequest.value.status_code === 128 ? '수정하시겠습니까?' : '제출하면 수정할 수 없습니다',
            text: selectedRequest.value.status_code === 128 ? '수정된 내용으로 다시 제출됩니다.' : '제출하시겠습니까?',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#6b7280',
            confirmButtonText: '확인',
            cancelButtonText: '취소',
            reverseButtons: true,
            customClass: {
                container: 'text-xs sm:text-sm md:text-base',
                title: 'text-sm sm:text-base md:text-lg',
                content: 'text-xs sm:text-sm md:text-base',
                confirmButton: 'text-xs sm:text-sm md:text-base',
                cancelButton: 'text-xs sm:text-sm md:text-base'
            }
        });

        if (confirmed.isConfirmed) {
            // 로딩 표시
            Swal.fire({
                title: '업로드중입니다',
                timerProgressBar: true,
                allowOutsideClick: false,
                didOpen: () => {
                    Swal.showLoading();
                },
                customClass: {
                    container: 'text-xs sm:text-sm md:text-base',
                    title: 'text-sm sm:text-base md:text-lg'
                }
            });

            // FormData 생성
            const formData = await createFormData();

            // 보완요청인 경우 수정 API 호출, 아닌 경우 제출 API 호출
            if (selectedRequest.value.status_code === 128) {
                await vmdAPI.updateReport(selectedRequest.value.id, formData);
            } else {
                await vmdAPI.submitReport(selectedRequest.value.id, formData);
            }

            // 상태 업데이트 (제출완료)
            await vmdAPI.updateStatus(selectedRequest.value.id, vmdAPI.VMD_STATUS.SUBMITTED);

            // 목록 업데이트
            updateRequestList(selectedRequest.value.id);

            // 성공 메시지
            await Swal.fire({
                icon: 'success',
                title: '성공',
                text: selectedRequest.value.status_code === 128 ? '수정이 완료되었습니다.' : '제출이 완료되었습니다.',
                customClass: {
                    container: 'text-xs sm:text-sm md:text-base',
                    title: 'text-sm sm:text-base md:text-lg',
                    content: 'text-xs sm:text-sm md:text-base',
                    confirmButton: 'text-xs sm:text-sm md:text-base'
                }
            });

            // 폼 초기화 및 상세보기 닫기
            selectedRequest.value = null;
            initSubmitForm();
        }
    } catch (error) {
        console.error('VMD 요청사항 제출 중 오류 발생:', error);
        Swal.fire({
            icon: 'error',
            title: '오류',
            text: error.response?.data?.error || '제출 중 오류가 발생했습니다.',
            customClass: {
                container: 'text-xs sm:text-sm md:text-base',
                title: 'text-sm sm:text-base md:text-lg',
                content: 'text-xs sm:text-sm md:text-base',
                confirmButton: 'text-xs sm:text-sm md:text-base'
            }
        });
    }
};

// FormData 생성 함수 분리
const createFormData = async () => {
    const formData = new FormData();
    formData.append('comment', submitForm.value.comment);
    formData.append('year', String(submitForm.value.year));  // 연도를 문자열로 변환하여 추가

    // 필수 이미지 추가
    if (submitForm.value.originalFiles.required1) {
        formData.append('pictures', submitForm.value.originalFiles.required1);
    }
    if (submitForm.value.originalFiles.required2) {
        formData.append('pictures', submitForm.value.originalFiles.required2);
    }

    // 추가 이미지 추가
    submitForm.value.originalFiles.additional.forEach(file => {
        formData.append('pictures', file);
    });

    // FormData 내용 확인
    for (let pair of formData.entries()) {
        console.log(pair[0] + ': ' + pair[1]);
    }

    return formData;
};

// 목록 업데이트 함수 분리
const updateRequestList = (requestId) => {
    requests.value = requests.value.map(r => {
        if (r.id === requestId) {
            return {
                ...r,
                status_code: vmdAPI.VMD_STATUS.SUBMITTED
            };
        }
        return r;
    });
};

// 제출 내용 보기 함수 수정
const viewSubmission = () => {
    if (selectedRequest.value) {
        const requestId = selectedRequest.value.id;
        // 현재 상세보기 닫기
        selectedRequest.value = null;
        // VMD 제출 목록 페이지로 이동 (request_id를 그대로 사용)
        router.push({
            name: 'VmdSubmissions',
            hash: `#submission-${requestId}`
        });
    }
};
</script>

<style scoped>
.slide-enter-active,
.slide-leave-active {
    transition: transform 0.3s ease;
}

.slide-enter-from,
.slide-leave-to {
    transform: translateX(100%);
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}

@media (min-width: 640px) {
    :deep(.pagination-text) {
        font-size: 12px;
    }

    :deep(.pagination-text .v-btn) {
        font-size: 12px;
    }
}

@media (min-width: 1024px) {
    :deep(.pagination-text) {
        font-size: 14px;
    }

    :deep(.pagination-text .v-btn) {
        font-size: 14px;
    }
}

@media (min-width: 1280px) {
    :deep(.pagination-text) {
        font-size: 15px;
    }

    :deep(.pagination-text .v-btn) {
        font-size: 15px;
    }
}

.swal-responsive-container {
    max-width: 18rem;
    font-size: 11px;
}

@media (min-width: 640px) {
    .swal-responsive-container {
        max-width: 22rem;
        font-size: 13px;
    }
}

@media (min-width: 768px) {
    .swal-responsive-container {
        max-width: 26rem;
        font-size: 15px;
    }
}

.swal-responsive-icon {
    width: 2rem !important;
    height: 2rem !important;
}

@media (min-width: 640px) {
    .swal-responsive-icon {
        width: 2.5rem !important;
        height: 2.5rem !important;
    }
}

.swal-responsive-title {
    font-size: 13px !important;
}

@media (min-width: 640px) {
    .swal-responsive-title {
        font-size: 15px !important;
    }
}

@media (min-width: 768px) {
    .swal-responsive-title {
        font-size: 17px !important;
    }

}

.swal-responsive-content {
    font-size: 11px !important;
}

@media (min-width: 640px) {
    .swal-responsive-content {
        font-size: 13px !important;
    }

}

.swal-responsive-button {
    font-size: 12px !important;
    padding: 0.5rem 1.2rem !important;
}

@media (min-width: 640px) {
    .swal-responsive-button {
        font-size: 14px !important;
    }
}
</style>